import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircleIcon, LockOpenIcon, XCircleIcon, XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Shared "Term Exams" screen (term paper marks entry) used by the teacher and admin pages.
// role: 'teacher' | 'admin'. Only meaningful when the school is in Term-Based grading mode.

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tx * { box-sizing:border-box; }
  .tx { font-family:'Sora',sans-serif; color:#1a1d2e; animation:txFade .4s ease both; }
  @keyframes txFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .tx-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.2rem; flex-wrap:wrap; gap:1rem; }
  .tx-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tx-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-amber { background:#fffbeb; color:#f59e0b; border:1px solid #fde68a; }
  .btn-amber:hover { background:#f59e0b; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  .tx-filters { display:flex; gap:14px; flex-wrap:wrap; margin-bottom:1.2rem; align-items:flex-end; }
  .tx-filters .form-field { min-width:200px; flex:1; max-width:340px; }
  .breadcrumb { display:flex; align-items:center; gap:6px; margin-bottom:1.2rem; flex-wrap:wrap; }
  .bc-item { font-size:.78rem; font-weight:600; color:#9094a8; cursor:pointer; transition:color .15s; }
  .bc-item:hover { color:#3b6ef8; }
  .bc-item.active { color:#0f1123; cursor:default; }
  .bc-sep { color:#d1d5db; font-size:.82rem; }
  .card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; margin-bottom:1rem; }
  .grade-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .grade-card:hover { border-color:#3b6ef8; box-shadow:0 4px 16px rgba(59,110,248,.1); transform:translateY(-1px); }
  .gc-name  { font-size:.95rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
  .gc-sub   { font-size:.72rem; color:#9094a8; }
  .gc-arrow { display:flex; align-items:center; justify-content:space-between; margin-top:10px; }
  .subj-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .subj-card:hover { border-color:#8b5cf6; box-shadow:0 4px 16px rgba(139,92,246,.12); transform:translateY(-1px); }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .row-item { display:flex; align-items:center; justify-content:space-between; padding:13px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; cursor:pointer; transition:background .12s; }
  .row-item:last-child { border-bottom:none; }
  .row-item:hover { background:#fafbff; }
  .row-title { font-size:.83rem; font-weight:600; color:#1a1d2e; }
  .row-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .status-badge { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; white-space:nowrap; }
  .status-badge.draft            { background:#f3f4f6; color:#6b7280; }
  .status-badge.submitted        { background:#ecfdf5; color:#10c98e; }
  .status-badge.locked           { background:#eef2ff; color:#3b6ef8; }
  .status-badge.unlock_requested { background:#fffbeb; color:#f59e0b; }
  .status-badge.unlocked         { background:#fdf4ff; color:#a855f7; }
  .info-box { background:#eef2ff; border:1px solid #c7d2fe; border-radius:12px; padding:10px 16px; margin-bottom:14px; font-size:.78rem; color:#3730a3; line-height:1.6; }
  .info-box.warn { background:#fffbeb; border-color:#fde68a; color:#92400e; }
  .marks-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.2rem; flex-wrap:wrap; gap:1rem; }
  .marks-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
  .marks-table { width:100%; border-collapse:collapse; }
  .marks-table thead { background:#fafbff; border-bottom:2px solid #edf0f9; }
  .marks-table th { padding:10px 12px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .marks-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .marks-table tbody tr:last-child { border-bottom:none; }
  .marks-table td { padding:8px 12px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .marks-input { width:80px; font-family:'JetBrains Mono',monospace; font-size:.82rem; padding:6px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; text-align:center; outline:none; }
  .marks-input:focus { border-color:#3b6ef8; background:white; }
  .marks-input:disabled { background:#f3f4f6; color:#b0b5cc; }
  .chk { width:16px; height:16px; cursor:pointer; }
  .student-name { font-weight:600; font-size:.8rem; color:#0f1123; }
  .student-roll { font-size:.7rem; color:#9094a8; margin-top:1px; font-family:'JetBrains Mono',monospace; }
  .marks-cards { display:none; padding:10px; }
  .marks-card { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .mc-name { font-weight:700; font-size:.84rem; color:#0f1123; margin-bottom:2px; }
  .mc-roll { font-size:.7rem; color:#9094a8; font-family:'JetBrains Mono',monospace; margin-bottom:8px; }
  .mc-row  { display:flex; align-items:center; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .mc-row:last-child { border-bottom:none; }
  .mc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .mc-checks { display:flex; gap:16px; }
  .mc-check-item { display:flex; align-items:center; gap:5px; font-size:.76rem; color:#6b7194; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input, .form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus, .form-select:focus { border-color:#3b6ef8; background:white; }
  .form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; resize:vertical; min-height:80px; }
  .form-textarea:focus { border-color:#3b6ef8; background:white; }
  .tx-empty   { text-align:center; padding:2.5rem 2rem; color:#9094a8; font-size:.82rem; }
  .tx-loading { display:flex; align-items:center; justify-content:center; height:160px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); font-family:'Sora',sans-serif; }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:16px; flex-wrap:wrap; }
  @media (max-width:768px) {
    .tx-title { font-size:1.1rem; }
    .card-grid { grid-template-columns:1fr 1fr; }
    .marks-table-wrap { display:none !important; }
    .marks-cards { display:block; }
    .marks-header { flex-direction:column; gap:10px; }
    .marks-actions .btn { flex:1; justify-content:center; }
    .tx-filters .form-field { max-width:none; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }
`;

const SUBJ_COLORS = ['#3b6ef8','#10c98e','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#84cc16','#f97316'];
const curYear = () => { const y = new Date().getFullYear(); return `${y}-${y + 1}`; };
const fmtNum = (n) => { const v = Number(n); return isFinite(v) ? String(Math.round(v * 100) / 100) : '—'; };
const idOf = (o) => o?._id ?? o?.id;
const secName = (s) => (typeof s === 'string' ? s : s?.name || String(s));
const statusLabel = (s) => (s || '').replace('_', ' ');

const ENDPOINTS = {
  teacher: {
    mode: '/teacher/assessment-mode', terms: '/teacher/term-periods', papers: '/teacher/term-papers',
    marks: (id) => `/teacher/assessments/${id}`, save: (id) => `/teacher/assessments/${id}/marks`,
  },
  admin: {
    mode: '/admin/settings/assessment-mode', terms: '/admin/term-periods', papers: '/admin/term-papers',
    marks: (id) => `/admin/assessments/${id}/marks`, save: (id) => `/admin/assessments/${id}/marks`,
  },
};

export default function TermExamsView({ role }) {
  const cfg = ENDPOINTS[role];
  const isAdmin = role === 'admin';

  const [loading,      setLoading]      = useState(true);
  const [isTermMode,   setIsTermMode]   = useState(false);
  const [terms,        setTerms]        = useState([]);
  const [classes,      setClasses]      = useState([]);
  const [mySubjects,   setMySubjects]   = useState([]);   // teacher: profile subjects; admin: all school subjects (fallback)
  const [termId,       setTermId]       = useState('');
  const [academicYear, setAcademicYear] = useState(curYear());

  const [navStep,       setNavStep]       = useState('classes'); // classes | sections | subjects | sheet
  const [activeClass,   setActiveClass]   = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const [activeSubject, setActiveSubject] = useState('');

  const [papers,      setPapers]      = useState([]);
  const [papersBusy,  setPapersBusy]  = useState(false);
  const [opening,     setOpening]     = useState(false);
  const [sheet,       setSheet]       = useState(null);
  const [marksData,   setMarksData]   = useState([]);
  const [saving,      setSaving]      = useState(false);

  const [unlockOpen,   setUnlockOpen]   = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [adminNote,    setAdminNote]    = useState('');

  const term = terms.find(t => String(t._id) === String(termId)) || null;

  // ── initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    const opts = { signal: ctrl.signal };
    (async () => {
      try {
        let mode = 'assessment';
        try { mode = (await api.get(cfg.mode, opts)).data?.assessmentMode || 'assessment'; } catch (e) { if (e.code === 'ERR_CANCELED') return; }
        if (mode !== 'term') { setIsTermMode(false); return; }
        setIsTermMode(true);
        const reqs = isAdmin
          ? [api.get(cfg.terms, opts), api.get('/admin/classes', opts), api.get('/admin/classes/subjects', opts).catch(() => ({ data: [] }))]
          : [api.get(cfg.terms, opts), api.get('/teacher/classes', opts), api.get('/teacher/profile', opts)];
        const [t, c, extra] = await Promise.all(reqs);
        const termList = (t.data || []).filter(x => x.active !== false);
        setTerms(termList);
        if (termList.length === 1) setTermId(String(termList[0]._id));
        setClasses(isAdmin ? (Array.isArray(c.data) ? c.data : (c.data?.classes || [])) : (c.data?.classes || []));
        setMySubjects(isAdmin ? (Array.isArray(extra.data) ? extra.data : []) : (extra.data?.subjects || []));
      } catch (err) {
        if (err.code !== 'ERR_CANCELED') toast.error('Failed to load');
      } finally { setLoading(false); }
    })();
    return () => ctrl.abort();
  }, [cfg, isAdmin]);

  // ── papers list for the chosen term ─────────────────────────────────────────
  const fetchPapers = useCallback(async () => {
    if (!termId) { setPapers([]); return; }
    try {
      setPapersBusy(true);
      const params = { termPeriodId: termId };
      if (academicYear.trim()) params.academicYear = academicYear.trim();
      const { data } = await api.get(cfg.papers, { params });
      setPapers(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load term papers'); }
    finally { setPapersBusy(false); }
  }, [cfg, termId, academicYear]);

  useEffect(() => { fetchPapers(); }, [fetchPapers]);

  const classById = (id) => classes.find(c => String(idOf(c)) === String(id));
  const paperFor = (classId, section, subject) =>
    papers.find(p => String(p.classId) === String(classId) && p.section === section && p.subject === subject);

  // ── navigation ──────────────────────────────────────────────────────────────
  const goToClasses  = () => { setNavStep('classes'); setActiveClass(null); setActiveSection(''); setActiveSubject(''); setSheet(null); };
  const goToSections = (cls) => { setActiveClass(cls); setActiveSection(''); setActiveSubject(''); setSheet(null); setNavStep('sections'); };
  const goToSubjects = (sec) => { setActiveSection(sec); setActiveSubject(''); setSheet(null); setNavStep('subjects'); };

  const subjectsFor = (cls) => {
    if (isAdmin) {
      const own = Array.isArray(cls?.subjects) ? cls.subjects.filter(Boolean) : [];
      return own.length ? own : mySubjects;
    }
    return mySubjects;
  };

  // ── marks sheet ─────────────────────────────────────────────────────────────
  const loadSheet = async (paperId) => {
    const { data } = await api.get(cfg.marks(paperId));
    setSheet(data);
    setMarksData((data.marks || []).map(m => {
      const marksObtained = m.marksObtained ?? m.marks_obtained ?? '';
      const isAbsent = !!(m.isAbsent || m.is_absent);
      const isNA = !!(m.isNA || m.is_na);
      return {
        studentId: m.student?._id || m.student_id || m.student,
        name: m.student?.user?.name || '—',
        rollNo: m.student?.rollNo || '',
        admissionNo: m.student?.admissionNo || '',
        marksObtained, isAbsent, isNA,
        remarks: m.remarks || '',
        wasEntered: marksObtained !== '' || isAbsent || isNA,
      };
    }));
    return data;
  };

  const openSubject = async (subject) => {
    if (!term) { toast.error('Select a term first'); return; }
    if (!academicYear.trim()) { toast.error('Enter the academic year'); return; }
    try {
      setOpening(true);
      const { data: paper } = await api.post(cfg.papers, {
        termPeriodId: termId, classId: idOf(activeClass), section: activeSection, subject, academicYear: academicYear.trim(),
      });
      await loadSheet(paper._id || paper.id);
      setActiveSubject(subject);
      setNavStep('sheet');
      fetchPapers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to open term paper');
    } finally { setOpening(false); }
  };

  const openExistingPaper = async (p) => {
    try {
      setOpening(true);
      await loadSheet(p._id);
      setActiveClass(classById(p.classId) || { _id: p.classId, name: `Class ${p.classId}` });
      setActiveSection(p.section);
      setActiveSubject(p.subject);
      setNavStep('sheet');
    } catch { toast.error('Failed to load term paper'); }
    finally { setOpening(false); }
  };

  const updateMark = (idx, field, value) => {
    setMarksData(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'isAbsent' && value) { next[idx].marksObtained = ''; next[idx].isNA = false; }
      if (field === 'isNA'     && value) { next[idx].marksObtained = ''; next[idx].isAbsent = false; }
      return next;
    });
  };

  const maxMarks = Number(sheet?.max_marks ?? sheet?.maxMarks) || 0;
  const status = sheet?.status || 'draft';
  const canEdit = isAdmin || ['draft', 'unlocked'].includes(status);

  // Returns the payload rows or null when validation fails. Blank, untouched rows are not sent
  // (the backend would otherwise store a blank as 0).
  const buildPayload = () => {
    const rows = [];
    for (const m of marksData) {
      const hasValue = m.marksObtained !== '' && m.marksObtained != null;
      if (!m.isAbsent && !m.isNA && hasValue) {
        const n = Number(m.marksObtained);
        if (isNaN(n) || n < 0 || n > maxMarks) { toast.error(`${m.name}: marks must be between 0 and ${fmtNum(maxMarks)}`); return null; }
      }
      if (!m.isAbsent && !m.isNA && !hasValue && m.remarks && !m.wasEntered) {
        toast.error(`${m.name}: enter marks (or tick Absent / N/A) before adding remarks`); return null;
      }
      if (m.isAbsent || m.isNA || hasValue || m.wasEntered) {
        rows.push({
          studentId: m.studentId,
          marksObtained: m.isAbsent || m.isNA || !hasValue ? null : Number(m.marksObtained),
          isAbsent: m.isAbsent, isNA: m.isNA, remarks: m.remarks,
        });
      }
    }
    return rows;
  };

  const handleSave = async (andSubmit = false) => {
    const rows = buildPayload();
    if (!rows) return;
    try {
      setSaving(true);
      if (rows.length) await api.put(cfg.save(sheet._id), { marks: rows });
      if (andSubmit) {
        await api.put(`/teacher/assessments/${sheet._id}/submit`);
        toast.success('Marks saved and submitted');
      } else {
        toast.success(isAdmin ? 'Marks saved' : 'Marks saved as draft');
      }
      await loadSheet(sheet._id);
      fetchPapers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save marks');
    } finally { setSaving(false); }
  };

  const handleRequestUnlock = async () => {
    if (!unlockReason.trim()) { toast.error('Please provide a reason'); return; }
    try {
      setSaving(true);
      await api.put(`/teacher/assessments/${sheet._id}/request-unlock`, { reason: unlockReason });
      toast.success('Unlock request sent');
      setUnlockOpen(false); setUnlockReason('');
      await loadSheet(sheet._id);
      fetchPapers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const handleReview = async (action) => {
    try {
      setSaving(true);
      await api.put(`/admin/assessments/${sheet._id}/review-unlock`, { action, adminNote });
      toast.success(action === 'approve' ? 'Unlocked successfully' : 'Request denied');
      setUnlockOpen(false); setAdminNote('');
      await loadSheet(sheet._id);
      fetchPapers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  // ── pieces ──────────────────────────────────────────────────────────────────
  const Crumbs = () => (
    <div className="breadcrumb">
      <span className={`bc-item ${navStep === 'classes' ? 'active' : ''}`} onClick={goToClasses}>Term Exams</span>
      {activeClass && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep === 'sections' ? 'active' : ''}`} onClick={() => goToSections(activeClass)}>{activeClass.name}</span>
      </>}
      {activeSection && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep === 'subjects' ? 'active' : ''}`} onClick={() => goToSubjects(activeSection)}>Section {activeSection}</span>
      </>}
      {navStep === 'sheet' && activeSubject && <>
        <span className="bc-sep">›</span><span className="bc-item active">{activeSubject}</span>
      </>}
    </div>
  );

  const Filters = () => (
    <div className="tx-filters">
      <div className="form-field">
        <label className="form-label">Term</label>
        <select className="form-select" value={termId} onChange={e => setTermId(e.target.value)}>
          <option value="">Select term…</option>
          {terms.map(t => (
            <option key={t._id} value={t._id}>{t.name}</option>
          ))}
        </select>
      </div>
      <div className="form-field">
        <label className="form-label">Academic Year</label>
        <input type="text" className="form-input" value={academicYear} placeholder="2025-2026"
          onChange={e => setAcademicYear(e.target.value)} />
      </div>
    </div>
  );

  const Header = ({ title, sub }) => (
    <div className="tx-header">
      <div><div className="tx-title">{title}</div><div className="tx-sub">{sub}</div></div>
    </div>
  );

  // ── guards ──────────────────────────────────────────────────────────────────
  if (loading) return <div className="tx"><style>{style}</style><div className="tx-loading"><div className="spinner" />Loading…</div></div>;

  if (!isTermMode) return (
    <div className="tx"><style>{style}</style>
      {Header({ title: "Term Exams", sub: "Enter term paper marks" })}
      <div className="panel"><div className="tx-empty">
        Term-Based grading is not enabled for this school.<br />
        {isAdmin ? 'Switch the grading mode to Term-Based in Settings to use Term Exams.' : 'Ask your administrator to enable Term-Based grading.'}
      </div></div>
    </div>
  );

  // ── MARKS SHEET ─────────────────────────────────────────────────────────────
  if (navStep === 'sheet' && sheet) {
    const paperPercent = Number(sheet.termWeight) || 0;
    const termName = term?.name || 'Term';
    return (
      <div className="tx"><style>{style}</style>
        {Crumbs()}
        <div className="marks-header">
          <div>
            <div className="tx-title">{termName} · Term Paper — {sheet.subject}</div>
            <div className="tx-sub">
              {sheet.class?.name || activeClass?.name} · Sec {sheet.section} · {sheet.academicYear || academicYear}
            </div>
            <div style={{fontSize:'.78rem',color:'#3730a3',marginTop:6,fontWeight:600}}>
              {termName} · Term Paper · counts as {fmtNum(paperPercent)}% of the term · marked out of {fmtNum(maxMarks)}
            </div>
            {sheet.createdBy?.name && isAdmin && (
              <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>Opened by: {sheet.createdBy.name}</div>
            )}
          </div>
          <div className="marks-actions">
            <span className={`status-badge ${status}`}>{statusLabel(status)}</span>
            {isAdmin ? (
              <>
                {status === 'unlock_requested' && (
                  <button className="btn btn-green btn-sm" onClick={() => { setAdminNote(''); setUnlockOpen(true); }}>
                    <LockOpenIcon />Review Unlock
                  </button>
                )}
                <button className="btn btn-blue" onClick={() => handleSave(false)} disabled={saving}>
                  <CheckCircleIcon />{saving ? 'Saving…' : 'Save Marks'}
                </button>
              </>
            ) : (
              <>
                {canEdit && <>
                  <button className="btn btn-ghost" onClick={() => handleSave(false)} disabled={saving}>Save</button>
                  <button className="btn btn-green" onClick={() => handleSave(true)} disabled={saving}>
                    <CheckCircleIcon />{saving ? 'Saving…' : 'Submit & lock'}
                  </button>
                </>}
                {['submitted', 'locked'].includes(status) && (
                  <button className="btn btn-amber" onClick={() => { setUnlockReason(''); setUnlockOpen(true); }}>
                    <LockOpenIcon />Request Unlock
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {isAdmin ? (
          <div className="info-box warn">✏️ <strong>Admin override:</strong> You can edit marks on this paper regardless of its status.</div>
        ) : !canEdit && (
          <div className="info-box">
            🔒 <strong>{statusLabel(status)}</strong>
            {status === 'unlock_requested' && ' — Unlock request pending admin approval.'}
            {['submitted', 'locked'].includes(status) && ' — Request unlock to edit marks.'}
          </div>
        )}

        {marksData.length === 0 ? (
          <div className="panel"><div className="tx-empty">No students found in this class and section.</div></div>
        ) : (
          <div className="panel">
            <div className="marks-table-wrap" style={{overflowX:'auto'}}>
              <table className="marks-table">
                <thead>
                  <tr><th>#</th><th>Student</th><th>Marks / {fmtNum(maxMarks)}</th><th>Absent</th><th>N/A</th><th>Remarks</th></tr>
                </thead>
                <tbody>
                  {marksData.map((m, i) => (
                    <tr key={m.studentId}>
                      <td style={{color:'#9094a8',fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem'}}>{i + 1}</td>
                      <td>
                        <div className="student-name">{m.name}</div>
                        <div className="student-roll">Roll: {m.rollNo || '—'}{m.admissionNo ? ` · ${m.admissionNo}` : ''}</div>
                      </td>
                      <td>
                        <input type="number" className="marks-input" value={m.marksObtained} min={0} max={maxMarks} step="any"
                          disabled={!canEdit || m.isAbsent || m.isNA}
                          onChange={e => updateMark(i, 'marksObtained', e.target.value)} />
                      </td>
                      <td><input type="checkbox" className="chk" checked={m.isAbsent} disabled={!canEdit} onChange={e => updateMark(i, 'isAbsent', e.target.checked)} /></td>
                      <td><input type="checkbox" className="chk" checked={m.isNA} disabled={!canEdit} onChange={e => updateMark(i, 'isNA', e.target.checked)} /></td>
                      <td>
                        <input type="text" className="marks-input" style={{width:120}} value={m.remarks} disabled={!canEdit}
                          placeholder="remarks…" onChange={e => updateMark(i, 'remarks', e.target.value)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="marks-cards">
              {marksData.map((m, i) => (
                <div key={m.studentId} className="marks-card">
                  <div className="mc-name">{m.name}</div>
                  <div className="mc-roll">Roll: {m.rollNo || '—'}{m.admissionNo ? ` · ${m.admissionNo}` : ''}</div>
                  <div className="mc-row">
                    <span className="mc-label">Marks / {fmtNum(maxMarks)}</span>
                    <input type="number" className="marks-input" value={m.marksObtained} min={0} max={maxMarks} step="any"
                      disabled={!canEdit || m.isAbsent || m.isNA}
                      onChange={e => updateMark(i, 'marksObtained', e.target.value)} />
                  </div>
                  <div className="mc-row">
                    <span className="mc-label">Status</span>
                    <div className="mc-checks">
                      <label className="mc-check-item"><input type="checkbox" checked={m.isAbsent} disabled={!canEdit} onChange={e => updateMark(i, 'isAbsent', e.target.checked)} /> Absent</label>
                      <label className="mc-check-item"><input type="checkbox" checked={m.isNA} disabled={!canEdit} onChange={e => updateMark(i, 'isNA', e.target.checked)} /> N/A</label>
                    </div>
                  </div>
                  <div className="mc-row">
                    <span className="mc-label">Remarks</span>
                    <input type="text" className="marks-input" style={{width:130}} value={m.remarks} disabled={!canEdit}
                      placeholder="remarks…" onChange={e => updateMark(i, 'remarks', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {unlockOpen && createPortal(
          <div className="modal-backdrop" onClick={() => !saving && setUnlockOpen(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">{isAdmin ? 'Review Unlock Request' : 'Request Unlock'}</div>
                <button className="modal-close" onClick={() => setUnlockOpen(false)}><XMarkIcon /></button>
              </div>
              {isAdmin ? (
                <>
                  {sheet.unlockRequest?.reason && (
                    <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:'.78rem',color:'#92400e'}}>
                      <strong style={{display:'block',marginBottom:3}}>Teacher's reason:</strong>{sheet.unlockRequest.reason}
                    </div>
                  )}
                  <div style={{fontSize:'.78rem',color:'#6b7194',marginBottom:14}}>
                    <strong style={{color:'#0f1123'}}>{termName} · Term Paper</strong> — {sheet.subject} — {sheet.class?.name || activeClass?.name} / {sheet.section}
                  </div>
                  <label className="form-label" style={{display:'block',marginBottom:5}}>Note to teacher (optional)</label>
                  <textarea className="form-textarea" value={adminNote} onChange={e => setAdminNote(e.target.value)} placeholder="Add a note…" />
                  <div className="modal-btns">
                    <button className="btn btn-ghost" onClick={() => setUnlockOpen(false)}>Cancel</button>
                    <button className="btn btn-red" onClick={() => handleReview('deny')} disabled={saving}><XCircleIcon />Deny</button>
                    <button className="btn btn-green" onClick={() => handleReview('approve')} disabled={saving}><LockOpenIcon />{saving ? 'Processing…' : 'Approve'}</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{fontSize:'.8rem',color:'#6b7194',marginBottom:14,lineHeight:1.6}}>
                    Explain why you need to edit this submitted term paper. Admin will review your request.
                  </div>
                  <label className="form-label" style={{display:'block',marginBottom:5}}>Reason *</label>
                  <textarea className="form-textarea" value={unlockReason} onChange={e => setUnlockReason(e.target.value)}
                    placeholder="e.g. Entered wrong marks for roll no. 5…" />
                  <div className="modal-btns">
                    <button className="btn btn-ghost" onClick={() => setUnlockOpen(false)}>Cancel</button>
                    <button className="btn btn-amber" onClick={handleRequestUnlock} disabled={saving}>
                      <LockOpenIcon />{saving ? 'Sending…' : 'Send Request'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  // ── no active terms ─────────────────────────────────────────────────────────
  if (terms.length === 0) return (
    <div className="tx"><style>{style}</style>
      {Header({ title: "Term Exams", sub: "Enter term paper marks" })}
      <div className="panel"><div className="tx-empty">
        No active terms found.<br />
        {isAdmin ? 'Open the Terms page and add a term.' : 'Ask your administrator to add a term (Terms page).'}
      </div></div>
    </div>
  );

  const termBanner = term && (
    <div className="info-box">
      <strong>{term.name}</strong> — select a subject below to enter its term paper marks. Each subject has its own paper share,
      set on the {isAdmin ? 'Subject Split page' : 'Subject Split page (Terms → Subject Split)'}.
    </div>
  );

  // ── SUBJECTS ────────────────────────────────────────────────────────────────
  if (navStep === 'subjects' && activeClass) {
    const subjects = subjectsFor(activeClass);
    return (
      <div className="tx"><style>{style}</style>
        {Crumbs()}
        {Header({ title: `Section ${activeSection}`, sub: `${activeClass.name} · Select a subject to enter its term paper marks` })}
        {Filters()}
        {termBanner}
        {!term ? <div className="tx-empty">Select a term above to continue.</div>
          : subjects.length === 0 ? <div className="tx-empty">No subjects found.</div> : (
          <div className="card-grid">
            {subjects.map((subj, i) => {
              const p = paperFor(idOf(activeClass), activeSection, subj);
              const col = SUBJ_COLORS[i % SUBJ_COLORS.length];
              return (
                <div key={subj} className="subj-card" style={opening ? {opacity:.6,pointerEvents:'none'} : undefined} onClick={() => openSubject(subj)}>
                  <div style={{width:32,height:32,borderRadius:9,background:`${col}18`,color:col,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.72rem',marginBottom:8}}>
                    {subj.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="gc-name">{subj}</div>
                  {p && (
                    <div className="gc-sub" style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
                      <span className={`status-badge ${p.status}`}>{statusLabel(p.status)}</span>
                      <span>{p.enteredCount} / {p.studentCount}</span>
                    </div>
                  )}
                  <div className="gc-arrow">
                    <span style={{fontSize:'.72rem',color:col,fontWeight:600}}>{opening ? 'Opening…' : 'Enter paper marks'}</span>
                    <ChevronRightIcon style={{width:15,height:15,stroke:col,strokeWidth:2}} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── SECTIONS ────────────────────────────────────────────────────────────────
  if (navStep === 'sections' && activeClass) {
    const secs = (activeClass.sections || []).map(secName);
    return (
      <div className="tx"><style>{style}</style>
        {Crumbs()}
        {Header({ title: activeClass.name, sub: "Select a section" })}
        {secs.length === 0 ? <div className="tx-empty">No sections found.</div> : (
          <div className="card-grid">
            {secs.map((sec, i) => {
              const col = SUBJ_COLORS[i % SUBJ_COLORS.length];
              return (
                <div key={sec} className="grade-card" onClick={() => goToSubjects(sec)}>
                  <div style={{width:36,height:36,borderRadius:10,background:`${col}18`,color:col,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.9rem',marginBottom:8}}>{sec}</div>
                  <div className="gc-name">Section {sec}</div>
                  <div className="gc-arrow">
                    <span style={{fontSize:'.72rem',color:col,fontWeight:600}}>View subjects</span>
                    <ChevronRightIcon style={{width:15,height:15,stroke:col,strokeWidth:2}} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── CLASSES (root) + existing papers ────────────────────────────────────────
  return (
    <div className="tx"><style>{style}</style>
      {Header({ title: "Term Exams", sub: isAdmin ? 'Enter or review term paper marks — choose a term, then a class' : 'Enter term paper marks — choose a term, then a class' })}
      {Filters()}
      {termBanner}
      {!term ? (
        <div className="panel"><div className="tx-empty">Select a term above to start.</div></div>
      ) : (
        <>
          {classes.length === 0 ? (
            <div className="tx-empty">{isAdmin ? 'No classes found.' : 'No classes assigned to you yet.'}</div>
          ) : (
            <div className="card-grid">
              {classes.map((cls, i) => {
                const col = SUBJ_COLORS[i % SUBJ_COLORS.length];
                const n = (cls.sections || []).length;
                return (
                  <div key={idOf(cls)} className="grade-card" onClick={() => goToSections(cls)}>
                    <div style={{width:36,height:36,borderRadius:10,background:`${col}18`,color:col,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.8rem',marginBottom:8}}>
                      {cls.name?.replace('Grade ', 'G').slice(0, 3)}
                    </div>
                    <div className="gc-name">{cls.name}</div>
                    <div className="gc-sub">{n} section{n !== 1 ? 's' : ''}</div>
                    <div className="gc-arrow">
                      <span style={{fontSize:'.72rem',color:'#3b6ef8',fontWeight:600}}>Select class</span>
                      <ChevronRightIcon style={{width:15,height:15,stroke:'#3b6ef8',strokeWidth:2}} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="panel">
            <div className="panel-head">
              <div className="panel-title">{isAdmin ? 'Term papers' : 'Your term papers'} — {term.name} ({papers.length})</div>
            </div>
            {papersBusy && papers.length === 0 ? (
              <div className="tx-loading"><div className="spinner" />Loading…</div>
            ) : papers.length === 0 ? (
              <div className="tx-empty">No term papers opened yet for {term.name}{academicYear.trim() ? ` (${academicYear.trim()})` : ''}.</div>
            ) : papers.map(p => (
              <div key={p._id} className="row-item" onClick={() => openExistingPaper(p)}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <div className="row-title">{p.subject}</div>
                    <span className={`status-badge ${p.status}`}>{statusLabel(p.status)}</span>
                  </div>
                  <div className="row-sub">
                    {classById(p.classId)?.name || `Class ${p.classId}`} · Section {p.section} · entered {p.enteredCount} / {p.studentCount} students · out of {fmtNum(p.maxMarks)}
                  </div>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <button className="btn btn-ghost btn-sm" disabled={opening} onClick={() => openExistingPaper(p)}>Open</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
